const { WOD_PART_TYPE, WOD_PART_ELEMENT_TYPE, WOD_SCORE_MODE } = require('../enums');

// NOTE Validation pure, sans dépendance serveur : elle est importée par le composant ReactJS CfcWodParts (navigateur) et par CfcWodController.
const MAX_PARTS = 2;
const PART_LABELS = [ 'A', 'B' ];
const DEFAULT_SINGLE_PART_NAME = 'WOD';
const DEFAULT_ROUNDS = 20;
const MAX_ROUNDS = 100;
const DEFAULT_ATTEMPTS = 5;
const MAX_ATTEMPTS = 20;

/**
 * @typedef {object} CfcWodPartElementObj
 * @property {number} position - Position de l'élément dans la partie
 * @property {string} type - Type de l'élément (mouvement ou tie break)
 * @property {?string} name - Nom du mouvement
 * @property {?number} quantity - Répétitions (AMRAP)
 * @property {?string} objective - Objectif (FOR TIME)
 */

/**
 * @typedef {object} CfcWodPartObj
 * @property {number} position - Position de la partie (1 = A, 2 = B)
 * @property {string} name - Nom de la partie
 * @property {string} type - Format de la partie
 * @property {?string} duration - Durée (AMRAP)
 * @property {?number} rounds - Nombre de tours à afficher (AMRAP)
 * @property {?string} time_cap - Time cap (FOR TIME)
 * @property {?string} max_movement - Mouvement à maximiser (MAX WEIGHT)
 * @property {?number} attempts - Nombre de tentatives (MAX WEIGHT, charge toujours en kg)
 * @property {?string} cash_in - Cash in
 * @property {?string} cash_out - Cash out
 * @property {boolean} has_tie_break_after_cash_in - Tie break après le cash in
 * @property {Array<CfcWodPartElementObj>} elements - Mouvements et tie breaks
 */

/**
 * @typedef {object} CfcWodPartsParsedObj
 * @property {Array<CfcWodPartObj>} parts - Parties normalisées
 * @property {?string} scoreMode - Gestion du score (uniquement s'il y a plusieurs parties)
 * @property {Array<string>} errors - Erreurs de validation
 */

/**
 * Retourne la lettre d'une partie
 * @param {number} position - Position de la partie (1 = A, 2 = B)
 * @returns {string} La lettre de la partie
 * TU DONE 🗸
 */
function getPartLabel(position) {
  return PART_LABELS[position - 1] || String(position);
}

/**
 * Nom par défaut d'une partie : « Partie A », « Partie B » s'il y a plusieurs parties, sinon « WOD »
 * @param {number} position - Position de la partie (1 = A, 2 = B)
 * @param {number} nbParts - Nombre de parties du wod
 * @returns {string} Le nom par défaut
 * TU DONE 🗸
 */
function getDefaultPartName(position, nbParts) {
  return nbParts > 1 ? `Partie ${ getPartLabel(position) }` : DEFAULT_SINGLE_PART_NAME;
}

/**
 * Nettoie une chaîne saisie : retourne null si elle est vide
 * @param {any} value - Valeur saisie
 * @returns {?string} La chaîne sans espaces superflus ou null
 * @private
 */
function _cleanString(value) {
  const cleaned = typeof value === 'string' ? value.trim() : '';

  return cleaned === '' ? null : cleaned;
}

/**
 * Convertit une valeur de formulaire en entier positif, ou retourne la valeur par défaut
 * @param {any} value - Valeur saisie
 * @param {?number} defaultValue - Valeur par défaut
 * @param {number} max - Valeur maximale autorisée
 * @returns {?number} Entier compris entre 1 et max, sinon la valeur par défaut
 * @private
 */
function _cleanPositiveInt(value, defaultValue, max) {
  const parsed = parseInt(value, 10);

  if (Number.isNaN(parsed) || parsed < 1) {
    return defaultValue;
  }
  return Math.min(parsed, max);
}

/**
 * Normalise un élément (mouvement ou tie break) selon le format de la partie
 * @param {any} rawElement - Élément saisi
 * @param {string} partType - Format de la partie
 * @param {number} position - Position de l'élément
 * @returns {CfcWodPartElementObj} L'élément normalisé
 * @private
 */
function _normalizeElement(rawElement, partType, position) {
  if (rawElement?.type === WOD_PART_ELEMENT_TYPE.TIE_BREAK.value) {
    return { position, type: WOD_PART_ELEMENT_TYPE.TIE_BREAK.value, name: null, quantity: null, objective: null };
  }

  return {
    position,
    type: WOD_PART_ELEMENT_TYPE.MOVEMENT.value,
    name: _cleanString(rawElement?.name),
    quantity: partType === WOD_PART_TYPE.AMRAP.value ? _cleanPositiveInt(rawElement?.quantity, null, Number.MAX_SAFE_INTEGER) : null,
    objective: partType === WOD_PART_TYPE.FOR_TIME.value ? _cleanString(rawElement?.objective) : null
  };
}

/**
 * Normalise une partie saisie : conversion des types et suppression des champs qui ne concernent pas son format
 * @param {any} rawPart - Partie saisie
 * @param {number} position - Position de la partie
 * @param {number} nbParts - Nombre de parties du wod
 * @returns {CfcWodPartObj} La partie normalisée
 * @private
 */
function _normalizePart(rawPart, position, nbParts) {
  const isKnownType = Object.values(WOD_PART_TYPE).some(partType => partType.value === rawPart?.type);
  const type = isKnownType ? rawPart.type : WOD_PART_TYPE.AMRAP.value;
  const isAmrap = type === WOD_PART_TYPE.AMRAP.value;
  const isForTime = type === WOD_PART_TYPE.FOR_TIME.value;
  const isMaxWeight = type === WOD_PART_TYPE.MAX_WEIGHT.value;
  const elements = !isMaxWeight && Array.isArray(rawPart?.elements) ? rawPart.elements : [];

  return {
    position,
    name: (nbParts > 1 && _cleanString(rawPart?.name)) || getDefaultPartName(position, nbParts),
    type,
    duration: isAmrap ? _cleanString(rawPart?.duration) : null,
    rounds: isAmrap ? _cleanPositiveInt(rawPart?.rounds, DEFAULT_ROUNDS, MAX_ROUNDS) : null,
    time_cap: isForTime ? _cleanString(rawPart?.time_cap) : null,
    max_movement: isMaxWeight ? _cleanString(rawPart?.max_movement) : null,
    attempts: isMaxWeight ? _cleanPositiveInt(rawPart?.attempts, DEFAULT_ATTEMPTS, MAX_ATTEMPTS) : null,
    cash_in: _cleanString(rawPart?.cash_in),
    cash_out: _cleanString(rawPart?.cash_out),
    has_tie_break_after_cash_in: Boolean(rawPart?.has_tie_break_after_cash_in) && _cleanString(rawPart?.cash_in) !== null,
    elements: elements.map((rawElement, index) => _normalizeElement(rawElement, type, index + 1))
  };
}

/**
 * Normalise les parties saisies (2 parties maximum)
 * @param {any} rawParts - Parties saisies
 * @returns {Array<CfcWodPartObj>} Les parties normalisées
 * TU DONE 🗸
 */
function normalizeParts(rawParts) {
  if (!Array.isArray(rawParts)) {
    return [];
  }

  const keptParts = rawParts.slice(0, MAX_PARTS);

  return keptParts.map((rawPart, index) => _normalizePart(rawPart, index + 1, keptParts.length));
}

/**
 * Valide les parties normalisées d'un wod
 * @param {Array<CfcWodPartObj>} parts - Parties normalisées
 * @returns {Array<string>} Les messages d'erreur (vide si tout est valide)
 * TU DONE 🗸
 */
function validateParts(parts) {
  const errors = [];

  parts.forEach(part => {
    // NOTE La lettre de la partie n'est précisée que s'il y a plusieurs parties
    const label = parts.length > 1 ? `Partie ${ getPartLabel(part.position) }` : 'WOD';
    const movements = part.elements.filter(element => element.type === WOD_PART_ELEMENT_TYPE.MOVEMENT.value);

    if (part.type === WOD_PART_TYPE.MAX_WEIGHT.value) {
      if (!part.max_movement) {
        errors.push(`${ label } : renseignez le mouvement.`);
      }
      return;
    }

    if (!movements.length) {
      errors.push(`${ label } : ajoutez au moins un mouvement.`);
    }
    movements.forEach((movement, index) => {
      if (!movement.name) {
        errors.push(`${ label } : le mouvement ${ index + 1 } n'a pas de nom.`);
      }
      if (part.type === WOD_PART_TYPE.AMRAP.value && !movement.quantity) {
        errors.push(`${ label } : renseignez les répétitions du mouvement ${ index + 1 }.`);
      }
    });
  });

  return errors;
}

/**
 * Détermine la gestion du score : elle n'existe que lorsque le wod a plusieurs parties
 * @param {any} scoreMode - Gestion du score saisie
 * @param {number} nbParts - Nombre de parties du wod
 * @returns {?string} La gestion du score ou null
 * TU DONE 🗸
 */
function normalizeScoreMode(scoreMode, nbParts) {
  if (nbParts < 2) {
    return null;
  }

  return scoreMode === WOD_SCORE_MODE.CUMULATIVE.value ? WOD_SCORE_MODE.CUMULATIVE.value : WOD_SCORE_MODE.SEPARATE.value;
}

/**
 * Analyse la découpe d'un wod envoyée par le formulaire (JSON) : normalisation puis validation
 * @param {any} json - Découpe saisie sous forme de JSON (chaîne) ou déjà décodée
 * @param {any} [scoreMode] - Gestion du score saisie
 * @returns {CfcWodPartsParsedObj} Les parties normalisées, la gestion du score et les erreurs
 * TU DONE 🗸
 */
function parseWodParts(json, scoreMode) {
  let rawParts = json;

  if (typeof json === 'string') {
    try {
      rawParts = json === '' ? [] : JSON.parse(json);
    } catch {
      return { parts: [], scoreMode: null, errors: ['La découpe du WOD est invalide.'] };
    }
  }

  const parts = normalizeParts(rawParts);

  return {
    parts,
    scoreMode: normalizeScoreMode(scoreMode, parts.length),
    errors: validateParts(parts)
  };
}

module.exports = {
  MAX_PARTS,
  getPartLabel,
  getDefaultPartName,
  normalizeParts,
  validateParts,
  normalizeScoreMode,
  parseWodParts
};
